/**
 * Builds the sections and links of the organization settings sidebar.
 * 1. Read the feature flags, permissions, and integrations that control each link.
 * 2. Make the list of navigation items for each section, and mark which items are shown.
 * 3. Put a divider after the last shown item of each group.
 * OrgSettingsLayout renders the result.
 */
/* eslint-disable max-lines */
import React, { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { useTranslation } from '@modernloop/i18n';
import { useFeatureFlagBool, useFlag, useSelectableLocales } from '@modernloop/shared/feature-flag';
import {
  AllSchedulingTasksIcon,
  AttributeIcon,
  CalendarClockIcon,
  CalendarWithArrowIcon,
  CandidatePortalIcon,
  ClockIcon,
  CloudWithKeyIcon,
  ComputerIcon,
  ContentCardIcon,
  DarkModeIcon,
  DashedSquareWithPlusIcon,
  DownloadIcon,
  DurationClockWithPlusIcon,
  GDPRIcon,
  HistoryIcon,
  HolidayIcon,
  InfoIcon,
  IntegrationsIcon,
  InterviewerPortalIcon,
  LanguagesIcon,
  LockIcon,
  MeetingRoomIcon,
  MultipleUsersIcon,
  NotificationIcon,
  PaperPlaneIcon,
  PreferencesIcon,
  SchedulingIcon,
  SparkleIcon,
  TemplateIcon,
  WrenchSetupIcon,
} from '@modernloop/shared/icons';

import { ApiMcpBetaTag } from 'src/components/ApiMcpBetaTag';

import { Permission, useHasUserPermission } from 'src/hooks/api/user/get';
import { useOrgAtsService } from 'src/hooks/atsService';
import useIsNewOrgExperience from 'src/hooks/useIsNewOrgExperience';
import { useIsPremiumCandidatePortalEnabled } from 'src/hooks/useIsPremiumCandidatePortalEnabled';
import useReturnTo from 'src/hooks/useReturnTo';

import { AccountSettingsTab, AccountSettingsUrlType, getAccountSettingUrls } from 'src/urls/getAccountSettingUrls';
import {
  OrganizationSettingsTab,
  OrganizationSettingsUrlType,
  getOrganizationSettingUrls,
} from 'src/urls/getOrganizationSettingUrls';

import { CodingToolParam } from 'src/views-new/Settings/CodingTool';
import { useConnectedIntegrations } from 'src/views-new/Settings/IntegrationSettings/useConnectedIntegrations';

// Nothing is optional
type NavType = {
  id: string;
  label: string;
  labelAdornment?: ReactNode;
  isShown: boolean;
  to: OrganizationSettingsUrlType | AccountSettingsUrlType;
  icon: ReactNode;
  isActive: boolean;
  selectedIcon: ReactNode;
  showDividerAfter?: boolean;
  /* Future scope if needed */
  // isDisabled : isNewOrgExperience
  // tooltip: string | undefined,
  // caption: string | undefined,
};

// Nothing is optional
type SettingsNavItem = {
  id: string;
  label: string;
  isShown: boolean;
  navs: NavType[];
  showDivider: boolean;
  hideSectionLabel?: boolean;

  /* Future scope if needed */
  // caption: string | undefined
  // isDisabled : isNewOrgExperience
  // tooltip: string | undefined,
  // infoIcon: string | undefined
};

/** Puts a divider after the last shown item of a group, so that hidden items do not leave a stray divider. */
const markDividerAfterLastVisible = (navs: NavType[], groupMemberIds: string[]): NavType[] => {
  const lastVisibleId = [...groupMemberIds].reverse().find((id) => navs.some((nav) => nav.id === id && nav.isShown));

  if (!lastVisibleId) {
    return navs;
  }

  return navs.map((nav) => (nav.id === lastVisibleId ? { ...nav, showDividerAfter: true } : nav));
};

/**
 * Builds visible organization settings sidebar sections and their promoted page links.
 */
export const useGetOrgSettingsNavItems = (): SettingsNavItem[] => {
  const { t } = useTranslation();
  const isNewOrgExperience = useIsNewOrgExperience();

  const returnTo = useReturnTo();
  const [isPremiumCandidatePortalEnabled] = useIsPremiumCandidatePortalEnabled();

  const canManageOrgSettings = useHasUserPermission(Permission.CanManageOrgSettings);
  const canReadAuditLogs = useHasUserPermission(Permission.ReadAuditLogs);
  const canSchedule = useHasUserPermission(Permission.CanSchedule);
  const hasDownloads = useFeatureFlagBool('settings_downloads');
  const aiProductEnabled = useFeatureFlagBool('AI_ENABLED');
  const knowledgeBasesEnabled = useFeatureFlagBool('org_knowledge_bases');
  const sessionInactivityTimeoutEnabled = useFeatureFlagBool('SESSION_INACTIVITY_TIMEOUT');
  const contentTranslationsEnabled = useFeatureFlagBool('org_content_translations');
  const collaborationLinksEnabled = useFeatureFlagBool('org_collaboration_link_settings');
  const zoomMeetingTopicSettingsEnabled = useFeatureFlagBool('org_zoom_meeting_topic_settings');
  // Coding tools retain their existing collaboration-links gate; this flag controls only the new
  // dedicated pages for the other connected integrations.
  const integrationSettingsPagesEnabled = useFeatureFlagBool('org_integration_settings_pages');
  const privacyAuditLogEnabled = useFeatureFlagBool('org_privacy_audit_log_ui');
  const activityFeedsEnabled = useFeatureFlagBool('NEW_ACTIVITY_FEEDS');
  // Translations requires BOTH the content-translations flag and the languages feature.
  const languagesEnabled = useSelectableLocales().length > 1;
  const showCandidatePortalConfigs = useFeatureFlagBool('org_candidate_portal_configs');
  const showCandidatePulseSurvey = useFeatureFlagBool('prd-205-candidate-pulse-survey');
  const isAdditionalAttendeesEnabled = useFeatureFlagBool('org_add_additional_event_attendees');
  const isDebriefAdditionalAttendeesSettingsEnabled = useFeatureFlagBool(
    'org_debrief_event_additional_attendees_settings'
  );
  // Gates the Debrief Location page. The flag name predates the setting broadening from a Zoom-host-only
  // control to a full location setting; renaming it is LaunchDarkly churn tracked as a follow-up.
  const isDebriefLocationEnabled = useFeatureFlagBool('org_debrief_default_zoom_host');
  const { connected: connectedIntegrations } = useConnectedIntegrations();
  const scorecardSyncEnabled = useFlag('org_ats_scorecard_sync');
  const atsService = useOrgAtsService();

  const { pathname, search } = useLocation();

  const params = new URLSearchParams(search);
  const selectedTool = params.get('tool');

  const codingToolBasePath = getOrganizationSettingUrls({ tab: OrganizationSettingsTab.CODING_TOOL });
  const showAccountApiKeys = canSchedule;
  const showAccountAuthorizedApps = canSchedule;
  const showOrgApiKeys = canManageOrgSettings;
  const showOrgAuthorizedApps = canManageOrgSettings;
  const showLegacyAccountNav = !isNewOrgExperience;
  const showAccountAppearance = useFeatureFlagBool('DARK_MODE');
  const apiMcpBetaTag = <ApiMcpBetaTag color="inherit" />;

  const topSettingsNavs = markDividerAfterLastVisible(
    markDividerAfterLastVisible(
      [
        {
          id: 'General',
          label: t('hiring-dashboard-nav:nav.settings.general'),
          selectedIcon: undefined,
          to: getAccountSettingUrls({ tab: AccountSettingsTab.GENERAL, returnTo }),
          icon: <PreferencesIcon color="inherit" />,
          isShown: showLegacyAccountNav,
          isActive: pathname === getAccountSettingUrls({ tab: AccountSettingsTab.GENERAL }),
        },
        {
          id: 'Appearance',
          label: t('hiring-dashboard-nav:nav.settings.appearance'),
          selectedIcon: undefined,
          to: getAccountSettingUrls({ tab: AccountSettingsTab.APPEARANCE, returnTo }),
          icon: <DarkModeIcon color="inherit" />,
          isShown: showAccountAppearance,
          isActive: pathname === getAccountSettingUrls({ tab: AccountSettingsTab.APPEARANCE }),
        },
        {
          id: 'My notifications',
          label: t('hiring-dashboard-nav:nav.settings.myNotifications'),
          selectedIcon: undefined,
          to: getAccountSettingUrls({ tab: AccountSettingsTab.NOTIFICATIONS, returnTo }),
          icon: <NotificationIcon color="inherit" />,
          isShown: showLegacyAccountNav,
          isActive: pathname === getAccountSettingUrls({ tab: AccountSettingsTab.NOTIFICATIONS }),
        },
        {
          id: 'Integrations',
          label: showLegacyAccountNav
            ? t('hiring-dashboard-nav:nav.settings.apps')
            : t('hiring-dashboard-nav:nav.settings.integrations'),
          selectedIcon: undefined,
          to: showLegacyAccountNav
            ? getAccountSettingUrls({ tab: AccountSettingsTab.APPS, returnTo })
            : getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATIONS, returnTo }),
          icon: <IntegrationsIcon color="inherit" />,
          isShown: true,
          isActive: showLegacyAccountNav
            ? pathname === getAccountSettingUrls({ tab: AccountSettingsTab.APPS })
            : pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATIONS }),
        },
        {
          id: 'Account Personal API keys',
          label: t('hiring-dashboard-nav:nav.settings.personalApiKeys'),
          labelAdornment: apiMcpBetaTag,
          selectedIcon: undefined,
          to: getAccountSettingUrls({ tab: AccountSettingsTab.API_KEYS, returnTo }),
          icon: <CloudWithKeyIcon color="inherit" />,
          isShown: showAccountApiKeys,
          isActive: pathname === getAccountSettingUrls({ tab: AccountSettingsTab.API_KEYS }),
        },
        {
          id: 'Account Authorized Apps',
          label: t('hiring-dashboard-nav:nav.settings.authorizedApps'),
          labelAdornment: apiMcpBetaTag,
          selectedIcon: undefined,
          to: getAccountSettingUrls({ tab: AccountSettingsTab.CONNECTED_APPS, returnTo }),
          icon: <IntegrationsIcon color="inherit" />,
          isShown: showAccountAuthorizedApps,
          isActive: pathname === getAccountSettingUrls({ tab: AccountSettingsTab.CONNECTED_APPS }),
        },
      ],
      ['General', 'Appearance', 'My notifications']
    ),
    ['Integrations', 'Account Personal API keys', 'Account Authorized Apps']
  );
  const showTopSettingsSection = topSettingsNavs.some((nav) => nav.isShown);
  const codingToolNavItems: NavType[] = (
    [
      { param: CodingToolParam.HACKERRANK, label: 'HackerRank', connected: connectedIntegrations.HACKERRANK },
      { param: CodingToolParam.CODESIGNAL, label: 'CodeSignal', connected: connectedIntegrations.CODESIGNAL },
      { param: CodingToolParam.CODERPAD, label: 'CoderPad', connected: connectedIntegrations.CODERPAD },
      { param: CodingToolParam.CODILITY, label: 'Codility', connected: connectedIntegrations.CODILITY },
    ] as const
  )
    .filter((tool) => collaborationLinksEnabled && tool.connected)
    .map((tool) => ({
      id: `Coding tool ${tool.param}`,
      label: tool.label,
      selectedIcon: undefined,
      to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.CODING_TOOL, tool: tool.param, returnTo }),
      icon: <ComputerIcon color="inherit" />,
      isShown: true,
      // The tab is a path param and the tool a query param, so match both.
      isActive: pathname === codingToolBasePath && selectedTool === tool.param,
    }));

  return [
    {
      id: 'Settings',
      label: t('hiring-dashboard-nav:nav.settings.title'),
      isShown: showTopSettingsSection,
      hideSectionLabel: true,
      showDivider: false,
      navs: topSettingsNavs,
    },
    {
      id: 'Interview Scheduling',
      label: t('hiring-dashboard-nav:nav.settings.scheduling'),
      isShown: !isNewOrgExperience,
      showDivider: false,
      navs: [
        {
          id: 'Availability requests',
          label: t('hiring-dashboard-nav:nav.settings.availabilityRequests'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({
            tab: OrganizationSettingsTab.CANDIDATE_AVAILABILITY_REQUESTS,
            returnTo,
          }),
          icon: <DurationClockWithPlusIcon color="inherit" />,
          isShown: true,
          isActive:
            pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.CANDIDATE_AVAILABILITY_REQUESTS }),
        },
        {
          id: 'Self-schedule requests',
          label: t('hiring-dashboard-nav:nav.settings.selfScheduleRequests'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({
            tab: OrganizationSettingsTab.SELF_SCHEDULE_REQUESTS,
            returnTo,
          }),
          icon: <CalendarWithArrowIcon color="inherit" />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.SELF_SCHEDULE_REQUESTS }),
        },
        {
          id: 'Schedule Settings',
          label: t('hiring-dashboard-settings-scheduling:settings.scheduling.tabGeneral'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.SCHEDULE_SETTINGS, returnTo }),
          icon: <PreferencesIcon color="inherit" />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.SCHEDULE_SETTINGS }),
        },
        {
          id: 'Calendar Event Keywords',
          label: t('hiring-dashboard-settings-scheduling:settings.scheduling.keywords.title'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.CALENDAR_EVENT_KEYWORDS, returnTo }),
          icon: <DashedSquareWithPlusIcon color="inherit" />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.CALENDAR_EVENT_KEYWORDS }),
        },
        {
          id: 'Candidate Communications',
          label: t('hiring-dashboard-settings-scheduling:settings.scheduling.communications.candidateConfirmation'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.CANDIDATE_COMMUNICATIONS, returnTo }),
          icon: <PaperPlaneIcon color="inherit" />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.CANDIDATE_COMMUNICATIONS }),
        },
        {
          id: 'Internal Communications',
          label: t('hiring-dashboard-settings-scheduling:settings.scheduling.communications.internalConfirmation'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTERNAL_COMMUNICATIONS, returnTo }),
          icon: <PaperPlaneIcon color="inherit" />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTERNAL_COMMUNICATIONS }),
        },
        {
          id: 'Debrief Settings',
          label: t('hiring-dashboard-settings-scheduling:settings.scheduling.communications.debrief'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.DEBRIEF_SETTINGS, returnTo }),
          icon: <SchedulingIcon color="inherit" />,
          isShown: isAdditionalAttendeesEnabled && isDebriefAdditionalAttendeesSettingsEnabled,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.DEBRIEF_SETTINGS }),
        },
        {
          id: 'Schedule Location',
          label: t('hiring-dashboard-settings-scheduling:settings.scheduling.meetingLocation.title'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.SCHEDULE_LOCATION, returnTo }),
          icon: <MeetingRoomIcon color="inherit" />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.SCHEDULE_LOCATION }),
        },
        {
          id: 'Debrief Location',
          label: t('hiring-dashboard-settings-scheduling:settings.scheduling.debriefLocation.title'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.DEBRIEF_LOCATION, returnTo }),
          icon: <MeetingRoomIcon color="inherit" />,
          isShown: isDebriefLocationEnabled,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.DEBRIEF_LOCATION }),
        },
        {
          id: 'Rescheduling',
          label: t('hiring-dashboard-settings-scheduling:settings.scheduling.rescheduling.title'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.RESCHEDULING, returnTo }),
          icon: <CalendarWithArrowIcon color="inherit" />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.RESCHEDULING }),
        },
        {
          id: 'Interview Plan Sync',
          label: t('hiring-dashboard-settings-scheduling:settings.scheduling.scorecardSync.title'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTERVIEW_PLAN_SYNC, returnTo }),
          icon: <CalendarWithArrowIcon color="inherit" />,
          isShown: scorecardSyncEnabled && !!atsService?.hasScorecardSync,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTERVIEW_PLAN_SYNC }),
        },
      ],
    },
    {
      id: 'Interviewers',
      label: t('hiring-dashboard-nav:nav.settings.interviewers'),
      isShown: !isNewOrgExperience,
      showDivider: false,
      navs: [
        {
          id: 'Interviewer Work Hours',
          label: t('hiring-dashboard-settings-interviewers:settings.interviewers.tabs.hoursAndAvailability'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTERVIEWER_WORK_HOURS, returnTo }),
          icon: <CalendarClockIcon color="inherit" />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTERVIEWER_WORK_HOURS }),
        },
        {
          id: 'Company days off',
          label: t('hiring-dashboard-nav:nav.settings.companyDaysOff'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({
            tab: OrganizationSettingsTab.COMPANY_DAYS_OFF,
            returnTo,
          }),
          icon: <HolidayIcon color="inherit" />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.COMPANY_DAYS_OFF }),
        },
        {
          id: 'Interviewer Load Limits',
          label: t('hiring-dashboard-settings-interviewers:settings.interviewers.interviewLoadLimits.title'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTERVIEWER_LOAD_LIMITS, returnTo }),
          icon: <CalendarClockIcon color="inherit" />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTERVIEWER_LOAD_LIMITS }),
        },
        {
          id: 'Interviewer Meeting Time',
          label: t('hiring-dashboard-settings-interviewers:settings.interviewers.meetingTime.title'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTERVIEWER_MEETING_TIME, returnTo }),
          icon: <CalendarClockIcon color="inherit" />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTERVIEWER_MEETING_TIME }),
        },
        {
          id: 'Interviewer Fields',
          label: t('hiring-dashboard-settings-interviewers:settings.interviewers.tabs.workInfo'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTERVIEWER_FIELDS, returnTo }),
          // This nav icon matches its full-opacity siblings; other InfoIcon uses keep the shared 55% opacity.
          icon: <InfoIcon color="inherit" sx={{ '& path': { fillOpacity: 1 } }} />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTERVIEWER_FIELDS }),
        },
        {
          id: 'Interviewer Custom Attributes',
          label: t('hiring-dashboard-settings-interviewers:settings.interviewers.tabs.customAttributes'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTERVIEWER_CUSTOM_ATTRIBUTES, returnTo }),
          icon: <AttributeIcon color="inherit" />,
          isShown: true,
          isActive:
            pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTERVIEWER_CUSTOM_ATTRIBUTES }),
        },
        {
          id: 'Interviewer Portal',
          label: t('hiring-dashboard-settings-interviewers:settings.interviewers.tabs.interviewerPortal'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTERVIEWER_PORTAL, returnTo }),
          icon: <InterviewerPortalIcon color="inherit" />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTERVIEWER_PORTAL }),
        },
      ],
    },
    {
      id: 'Tasks',
      label: t('hiring-dashboard-nav:nav.settings.tasks'),
      isShown: !isNewOrgExperience,
      showDivider: false,
      navs: [
        {
          id: 'General',
          label: t('hiring-dashboard-settings-tasks:settings.tasks.tabGeneral'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.TASK_SETTINGS, returnTo }),
          icon: <PreferencesIcon color="inherit" />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.TASK_SETTINGS }),
        },
        {
          id: 'Task tags',
          label: t('hiring-dashboard-settings-tasks:settings.tasks.tabTaskTags'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.TASK_TAGS, returnTo }),
          icon: <TemplateIcon color="inherit" />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.TASK_TAGS }),
        },
        {
          id: 'Queues',
          label: t('hiring-dashboard-settings-tasks:settings.tasks.tabQueues'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.TASK_QUEUES, returnTo }),
          icon: <AllSchedulingTasksIcon color="inherit" />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.TASK_QUEUES }),
        },
        {
          id: 'Reschedule reasons',
          label: t('hiring-dashboard-settings-tasks:settings.tasks.tabRescheduleReasons'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.TASK_RESCHEDULE_REASONS, returnTo }),
          icon: <CalendarWithArrowIcon color="inherit" />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.TASK_RESCHEDULE_REASONS }),
        },
        {
          id: 'Send-on-behalf-of permissions',
          label: t('hiring-dashboard-settings-tasks:settings.tasks.tabSendOnBehalfOf'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({
            tab: OrganizationSettingsTab.TASK_SEND_ON_BEHALF_OF_PERMISSIONS,
            returnTo,
          }),
          icon: <MultipleUsersIcon color="inherit" />,
          isShown: true,
          isActive:
            pathname ===
            getOrganizationSettingUrls({ tab: OrganizationSettingsTab.TASK_SEND_ON_BEHALF_OF_PERMISSIONS }),
        },
      ],
    },
    {
      id: 'Candidate Portal',
      label: t('hiring-dashboard-nav:nav.settings.candidatePortal'),
      isShown: !!(!isNewOrgExperience && isPremiumCandidatePortalEnabled),
      showDivider: false,
      navs: [
        {
          id: 'Setup',
          label: t('hiring-dashboard-settings-candidate-portal:settings.candidatePortal.setup.header'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.CANDIDATE_PORTAL_SETUP, returnTo }),
          icon: <CandidatePortalIcon color="inherit" />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.CANDIDATE_PORTAL_SETUP }),
        },
        {
          id: 'Branding themes',
          label: t('hiring-dashboard-settings-candidate-portal:settings.candidatePortal.brandingThemes.header'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({
            tab: OrganizationSettingsTab.CANDIDATE_PORTAL_BRANDING_THEMES,
            returnTo,
          }),
          icon: <TemplateIcon color="inherit" />,
          isShown: true,
          isActive:
            pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.CANDIDATE_PORTAL_BRANDING_THEMES }),
        },
        {
          id: 'Page templates',
          label: t('hiring-dashboard-settings-candidate-portal:settings.candidatePortal.pageTemplates.header'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({
            tab: OrganizationSettingsTab.CANDIDATE_PORTAL_PAGE_TEMPLATES,
            returnTo,
          }),
          icon: <ContentCardIcon color="inherit" />,
          isShown: true,
          isActive:
            pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.CANDIDATE_PORTAL_PAGE_TEMPLATES }),
        },
        {
          id: 'Portals',
          label: t('hiring-dashboard-settings-candidate-portal:settings.candidatePortal.portalConfigs.header'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.CANDIDATE_PORTAL_PORTALS, returnTo }),
          icon: <CandidatePortalIcon color="inherit" />,
          isShown: showCandidatePortalConfigs,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.CANDIDATE_PORTAL_PORTALS }),
        },
        {
          id: 'Automation',
          label: t('hiring-dashboard-settings-candidate-portal:settings.candidatePortal.automation.header'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.CANDIDATE_PORTAL_AUTOMATION, returnTo }),
          icon: <SchedulingIcon color="inherit" />,
          isShown: showCandidatePortalConfigs,
          isActive:
            pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.CANDIDATE_PORTAL_AUTOMATION }),
        },
        {
          id: 'Candidate pulse survey',
          label: t('hiring-dashboard-settings-candidate-portal:settings.candidatePortal.pulseSurvey.header'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.CANDIDATE_PORTAL_PULSE_SURVEY, returnTo }),
          icon: <PaperPlaneIcon color="inherit" />,
          isShown: showCandidatePulseSurvey,
          isActive:
            pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.CANDIDATE_PORTAL_PULSE_SURVEY }),
        },
      ],
    },
    {
      id: 'Workspace',
      label: t('hiring-dashboard-nav:nav.settings.workspace'),
      isShown: true,
      showDivider: false,
      navs: [
        {
          id: 'Meeting rooms',
          label: t('hiring-dashboard-nav:nav.settings.meetingRooms'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({
            tab: OrganizationSettingsTab.MEETING_ROOMS,
            returnTo,
          }),
          icon: <MeetingRoomIcon color="inherit" />,
          isShown: !isNewOrgExperience,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.MEETING_ROOMS }),
        },
        {
          id: 'Templates',
          label: t('hiring-dashboard-nav:nav.settings.templates'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.TEMPLATES, returnTo }),
          icon: <TemplateIcon color="inherit" />,
          isShown: !isNewOrgExperience,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.TEMPLATES }),
        },
        {
          id: 'Knowledge bases',
          label: t('hiring-dashboard-nav:nav.settings.knowledgeBases'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.KNOWLEDGE_BASES, returnTo }),
          icon: <ContentCardIcon color="inherit" />,
          isShown: !isNewOrgExperience && knowledgeBasesEnabled,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.KNOWLEDGE_BASES }),
        },
        {
          id: 'Notifications for others',
          label: t('hiring-dashboard-nav:nav.settings.notificationsForOthers'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.NOTIFICATIONS, returnTo }),
          icon: <NotificationIcon color="inherit" />,
          isShown: !isNewOrgExperience,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.NOTIFICATIONS }),
        },
        {
          id: 'AI',
          label: t('hiring-dashboard-nav:nav.settings.ai'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.AI, returnTo }),
          icon: <SparkleIcon color="inherit" />,
          isShown: !isNewOrgExperience && aiProductEnabled,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.AI }),
        },
        {
          id: 'Integrations',
          label: t('hiring-dashboard-nav:nav.settings.integrations'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATIONS, returnTo }),
          icon: <IntegrationsIcon color="inherit" />,
          isShown: showLegacyAccountNav,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATIONS }),
        },
        {
          id: 'Members',
          label: t('hiring-dashboard-nav:nav.settings.members'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.MEMBERS, returnTo }),
          icon: <MultipleUsersIcon color="inherit" />,
          isShown: true,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.MEMBERS }),
        },
        {
          id: 'Setup',
          label: t('hiring-dashboard-nav:nav.settings.setup'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.SETUP, returnTo }),
          icon: <WrenchSetupIcon color="inherit" />,
          isShown: !isNewOrgExperience,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.SETUP }),
        },
        {
          id: 'Translations',
          label: t('hiring-dashboard-nav:nav.settings.translations'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.TRANSLATIONS, returnTo }),
          icon: <LanguagesIcon color="inherit" />,
          isShown: !isNewOrgExperience && canManageOrgSettings && contentTranslationsEnabled && languagesEnabled,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.TRANSLATIONS }),
        },
      ],
    },
    {
      id: 'Org Integrations',
      label: t('hiring-dashboard-nav:nav.settings.integrationsSection'),
      // Dedicated pages appear only when their rollout flag is on and a connection exists; setup remains on the hub.
      // Coding tools count via codingToolNavItems (not connectedIntegrations) because their rows are
      // additionally flag-gated — otherwise the header could render with zero items.
      isShown:
        (integrationSettingsPagesEnabled &&
          [
            connectedIntegrations.GREENHOUSE,
            connectedIntegrations.LEVER,
            connectedIntegrations.ASHBY,
            connectedIntegrations.WORKDAY,
            connectedIntegrations.MICROSOFT,
            connectedIntegrations.MICROSOFT_GCC_HIGH,
            connectedIntegrations.MICROSOFT_SERVICE_USER,
            connectedIntegrations.GOOGLE_WORKSPACE,
            connectedIntegrations.SMARTRECRUITERS,
            connectedIntegrations.GEM,
            connectedIntegrations.SLACK,
          ].some(Boolean)) ||
        (zoomMeetingTopicSettingsEnabled && connectedIntegrations.ZOOM) ||
        codingToolNavItems.length > 0,
      showDivider: false,
      navs: [
        {
          id: 'Greenhouse',
          label: t('hiring-dashboard-nav:nav.settings.integrationGreenhouse'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_GREENHOUSE, returnTo }),
          icon: <IntegrationsIcon color="inherit" />,
          isShown: integrationSettingsPagesEnabled && connectedIntegrations.GREENHOUSE,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_GREENHOUSE }),
        },
        {
          id: 'Lever',
          label: t('hiring-dashboard-nav:nav.settings.integrationLever'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_LEVER, returnTo }),
          icon: <IntegrationsIcon color="inherit" />,
          isShown: integrationSettingsPagesEnabled && connectedIntegrations.LEVER,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_LEVER }),
        },
        {
          id: 'Ashby',
          label: t('hiring-dashboard-nav:nav.settings.integrationAshby'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_ASHBY, returnTo }),
          icon: <IntegrationsIcon color="inherit" />,
          isShown: integrationSettingsPagesEnabled && connectedIntegrations.ASHBY,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_ASHBY }),
        },
        {
          id: 'Workday',
          label: t('hiring-dashboard-nav:nav.settings.integrationWorkday'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_WORKDAY, returnTo }),
          icon: <IntegrationsIcon color="inherit" />,
          isShown: integrationSettingsPagesEnabled && connectedIntegrations.WORKDAY,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_WORKDAY }),
        },
        {
          id: 'Microsoft 365',
          label: t('hiring-dashboard-nav:nav.settings.integrationMicrosoft'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_MICROSOFT, returnTo }),
          icon: <IntegrationsIcon color="inherit" />,
          isShown: integrationSettingsPagesEnabled && connectedIntegrations.MICROSOFT,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_MICROSOFT }),
        },
        {
          id: 'Microsoft GCC High',
          label: t('hiring-dashboard-nav:nav.settings.integrationMicrosoftGccHigh'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_MICROSOFT_GCC_HIGH, returnTo }),
          icon: <IntegrationsIcon color="inherit" />,
          isShown: integrationSettingsPagesEnabled && connectedIntegrations.MICROSOFT_GCC_HIGH,
          isActive:
            pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_MICROSOFT_GCC_HIGH }),
        },
        {
          id: 'Microsoft Service User',
          label: t('hiring-dashboard-nav:nav.settings.integrationMicrosoftServiceUser'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_MICROSOFT_SERVICE_USER, returnTo }),
          icon: <IntegrationsIcon color="inherit" />,
          isShown: integrationSettingsPagesEnabled && connectedIntegrations.MICROSOFT_SERVICE_USER,
          isActive:
            pathname ===
            getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_MICROSOFT_SERVICE_USER }),
        },
        {
          id: 'Google Workspace',
          label: t('hiring-dashboard-nav:nav.settings.integrationGoogleWorkspace'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_GOOGLE_WORKSPACE, returnTo }),
          icon: <IntegrationsIcon color="inherit" />,
          isShown: integrationSettingsPagesEnabled && connectedIntegrations.GOOGLE_WORKSPACE,
          isActive:
            pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_GOOGLE_WORKSPACE }),
        },
        {
          id: 'SmartRecruiters',
          label: t('hiring-dashboard-nav:nav.settings.integrationSmartRecruiters'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_SMARTRECRUITERS, returnTo }),
          icon: <IntegrationsIcon color="inherit" />,
          isShown: integrationSettingsPagesEnabled && connectedIntegrations.SMARTRECRUITERS,
          isActive:
            pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_SMARTRECRUITERS }),
        },
        {
          id: 'Gem',
          label: t('hiring-dashboard-nav:nav.settings.integrationGem'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_GEM, returnTo }),
          icon: <IntegrationsIcon color="inherit" />,
          isShown: integrationSettingsPagesEnabled && connectedIntegrations.GEM,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_GEM }),
        },
        {
          id: 'Slack',
          label: t('hiring-dashboard-nav:nav.settings.integrationSlack'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_SLACK, returnTo }),
          icon: <IntegrationsIcon color="inherit" />,
          isShown: integrationSettingsPagesEnabled && connectedIntegrations.SLACK,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_SLACK }),
        },
        {
          id: 'Zoom',
          label: t('hiring-dashboard-nav:nav.settings.integrationZoom'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_ZOOM, returnTo }),
          icon: <IntegrationsIcon color="inherit" />,
          isShown: zoomMeetingTopicSettingsEnabled && connectedIntegrations.ZOOM,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATION_ZOOM }),
        },
        ...codingToolNavItems,
      ],
    },
    {
      id: 'Security & Compliance',
      label: t('hiring-dashboard-nav:nav.settings.securityAndCompliance'),
      isShown: true,
      showDivider: false,
      navs: [
        {
          id: 'Privacy',
          label: t('hiring-dashboard-nav:nav.settings.privacy'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.PRIVACY, returnTo }),
          icon: <LockIcon color="inherit" />,
          isShown: !isNewOrgExperience,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.PRIVACY }),
        },
        {
          id: 'Privacy Audit Log',
          label: t('hiring-dashboard-nav:nav.settings.privacyAuditLog'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.PRIVACY_AUDIT_LOG, returnTo }),
          icon: <HistoryIcon color="inherit" />,
          // Compliance surface: hide the entry entirely for Employees without READ_AUDIT_LOGS.
          // This is owner-only by product decision, so admins must not see the page.
          // Gate on the same rollout flag as the page/GraphQL query/CSV export.
          isShown: canReadAuditLogs && privacyAuditLogEnabled,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.PRIVACY_AUDIT_LOG }),
        },
        {
          id: 'Organization Activity Log',
          label: t('hiring-dashboard-settings-privacy:settings.organizationActivityLog.title'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.ORG_ACTIVITY_LOG, returnTo }),
          icon: <HistoryIcon color="inherit" />,
          isShown: canManageOrgSettings && activityFeedsEnabled,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.ORG_ACTIVITY_LOG }),
        },
        {
          id: 'Session timeout',
          label: t('hiring-dashboard-nav:nav.settings.sessionTimeout'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.SESSION_TIMEOUT, returnTo }),
          icon: <ClockIcon color="inherit" />,
          isShown: !isNewOrgExperience && sessionInactivityTimeoutEnabled,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.SESSION_TIMEOUT }),
        },
        {
          id: 'Organization API keys',
          label: t('hiring-dashboard-nav:nav.settings.apiKeys'),
          labelAdornment: apiMcpBetaTag,
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.API_KEYS, returnTo }),
          icon: <CloudWithKeyIcon color="inherit" />,
          isShown: showOrgApiKeys,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.API_KEYS }),
        },
        {
          id: 'Organization Authorized Apps',
          label: t('hiring-dashboard-nav:nav.settings.authorizedApps'),
          labelAdornment: apiMcpBetaTag,
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.AUTHORIZED_APPS, returnTo }),
          icon: <IntegrationsIcon color="inherit" />,
          isShown: showOrgAuthorizedApps,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.AUTHORIZED_APPS }),
        },
        {
          id: 'API Audit Log',
          label: t('hiring-dashboard-nav:nav.settings.apiAuditLog'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.API_AUDIT_LOG, returnTo }),
          icon: <CloudWithKeyIcon color="inherit" />,
          isShown: canReadAuditLogs,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.API_AUDIT_LOG }),
        },
        {
          id: 'MCP Audit Log',
          label: t('hiring-dashboard-nav:nav.settings.mcpAuditLog'),
          labelAdornment: apiMcpBetaTag,
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.MCP_AUDIT_LOG, returnTo }),
          icon: <CloudWithKeyIcon color="inherit" />,
          isShown: canReadAuditLogs,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.MCP_AUDIT_LOG }),
        },
        {
          id: 'Secrets',
          label: t('hiring-dashboard-nav:nav.settings.secrets'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.SECRETS, returnTo }),
          icon: <CloudWithKeyIcon color="inherit" />,
          isShown: canManageOrgSettings,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.SECRETS }),
        },
        {
          id: 'Downloads',
          label: t('hiring-dashboard-nav:nav.settings.downloads'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.DOWNLOADS, returnTo }),
          icon: <DownloadIcon color="inherit" />,
          isShown: canManageOrgSettings && hasDownloads,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.DOWNLOADS }),
        },
        {
          id: 'GDPR/CCPA',
          label: t('hiring-dashboard-nav:nav.settings.gdprCcpa'),
          selectedIcon: undefined,
          to: getOrganizationSettingUrls({ tab: OrganizationSettingsTab.GDPR_CCPA, returnTo }),
          icon: <GDPRIcon color="inherit" />,
          isShown: !isNewOrgExperience,
          isActive: pathname === getOrganizationSettingUrls({ tab: OrganizationSettingsTab.GDPR_CCPA }),
        },
      ],
    },
  ];
};
